import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './BattleComplete.css';

const BattleComplete = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // FIXED: Match the actual URL parameters from BattleWorkout navigation
    const { battleCode: urlBattleCode, finalScore, totalReps, sets, completedSets } = useParams();

    // Email functionality states
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Try to get battleCode from multiple sources
    const getBattleCode = () => {
        // First try URL params
        if (urlBattleCode && urlBattleCode !== 'undefined' && urlBattleCode !== 'unknown') {
            console.log('Battle code found in URL params:', urlBattleCode);
            return urlBattleCode;
        }

        // Then try location state
        if (location.state?.battleCode) {
            console.log('Battle code found in location state:', location.state.battleCode);
            return location.state.battleCode;
        }

        // Then try sessionStorage from BattleComplete data
        try {
            const battleCompleteData = sessionStorage.getItem('battleCompleteData');
            if (battleCompleteData) {
                const data = JSON.parse(battleCompleteData);
                if (data.battleCode && data.battleCode !== 'undefined') {
                    console.log('Battle code found in session battleCompleteData:', data.battleCode);
                    return data.battleCode;
                }
            }
        } catch (e) {
            console.log('Error parsing battleCompleteData from session storage');
        }

        // Then try direct sessionStorage
        const sessionBattleCode = sessionStorage.getItem('currentBattleCode');
        if (sessionBattleCode && sessionBattleCode !== 'undefined') {
            console.log('Battle code found in session storage:', sessionBattleCode);
            return sessionBattleCode;
        }

        // Then try localStorage
        const localBattleCode = localStorage.getItem('lastBattleCode');
        if (localBattleCode && localBattleCode !== 'undefined') {
            console.log('Battle code found in local storage:', localBattleCode);
            return localBattleCode;
        }

        console.warn('Battle code not found in any source');
        return null;
    };

    const actualBattleCode = getBattleCode();

    // State for battle completion
    const [showConfetti, setShowConfetti] = useState(true);
    const [bonusPointsApplied, setBonusPointsApplied] = useState(false);
    const [bonusPoints, setBonusPoints] = useState(0);
    const [adjustedTotalScore, setAdjustedTotalScore] = useState(parseInt(finalScore) || 0);
    const [generatedWorkout, setGeneratedWorkout] = useState([]);

    // Debug logging
    useEffect(() => {
        console.log('BattleComplete Debug Info:');
        console.log('URL Params:', { urlBattleCode, finalScore, totalReps, sets, completedSets });
        console.log('Location state:', location.state);
        console.log('Session storage currentBattleCode:', sessionStorage.getItem('currentBattleCode'));
        console.log('Session storage battleCompleteData:', sessionStorage.getItem('battleCompleteData'));
        console.log('Local storage lastBattleCode:', localStorage.getItem('lastBattleCode'));
        console.log('Final battle code:', actualBattleCode);
    }, []);

    // Get random background image
    const [backgroundImage] = useState(() => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    });

    // Create branded battle workout summary with watermark
    const createBrandedBattleSummary = () => {
        const workoutDate = new Date().toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        });

        const exerciseCounts = generatedWorkout.reduce((acc, exercise) => {
            acc[exercise] = (acc[exercise] || 0) + 1;
            return acc;
        }, {});

        const summary = `🎮 BATTLE COMPLETE! ⚔️

🏆 VICTORY ACHIEVED! 🏆

📊 BATTLE STATS:
• Total Score: ${adjustedTotalScore}${bonusPoints > 0 ? ` (includes +${bonusPoints} Health Bonus)` : ''}
• Battle Code: ${actualBattleCode || 'unknown'}
• Workout Type: ${getWorkoutType()}
• Total Reps: ${totalReps || 0}
• Total Sets: ${sets || 0}
• Completed Sets: ${completedSets || 0}
• Unique Moves: ${Object.keys(exerciseCounts).length}
• Date: ${workoutDate}

⚔️ YOUR BATTLE WORKOUT:
${Object.entries(exerciseCounts).map(([exercise, count], index) =>
            `${index + 1}. ${count > 1 ? `${exercise} (x${count})` : exercise}`
        ).join('\n')}

${bonusPoints > 0 ? `\n💪 HIDDEN HEALTH BONUS: +${bonusPoints} points from your workout history!\n` : ''}

────────────────────────────────
🔥 BUMFIT - Train - Build - Battle 🔥
bumfit.com - Where every word is a workout!
Unlimited workouts at your fingertips.
────────────────────────────────`;

        return summary;
    };

    // Handle email button click
    const handleEmailWorkout = () => {
        setShowEmailModal(true);
        soundManager.playSound('next', 0.6);
    };

    // Submit email request using EmailJS
    const handleSubmitEmail = async () => {
        if (!userEmail || !userEmail.includes('@')) {
            alert('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);

        try {
            // Initialize EmailJS with your public key
            if (window.emailjs) {
                window.emailjs.init('Ke_xwVYFVvuHuFKnX');
            } else {
                throw new Error('EmailJS not loaded. Please refresh the page and try again.');
            }

            const workoutSummary = createBrandedBattleSummary();
            const exerciseCounts = generatedWorkout.reduce((acc, exercise) => {
                acc[exercise] = (acc[exercise] || 0) + 1;
                return acc;
            }, {});

            // Prepare template parameters for EmailJS
            const templateParams = {
                to_email: userEmail,
                workout_title: `🎮 BATTLE COMPLETE! ⚔️`,
                exercise_count: Object.keys(exerciseCounts).length,
                total_reps: totalReps || 0,
                workout_summary: workoutSummary,
                timestamp: new Date().toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric'
                })
            };

            console.log('Sending battle email with EmailJS:', templateParams);

            // Send email using EmailJS
            const response = await window.emailjs.send(
                'service_d5cc5ls',     // Your service ID
                'template_af7akil',    // Your template ID
                templateParams,
                'Ke_xwVYFVvuHuFKnX'    // Your public key
            );

            console.log('Battle email sent successfully:', response);
            alert('🎮 Battle results sent to your email! Check your inbox. ⚔️');
            setShowEmailModal(false);
            setUserEmail('');

        } catch (error) {
            console.error('EmailJS error:', error);
            alert('Error sending battle results: ' + (error.text || error.message || 'Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Calculate Hidden Health Bonus from saved workouts
    const calculateHiddenHealthBonus = () => {
        try {
            const savedWorkouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');
            let totalPastReps = 0;

            savedWorkouts.forEach(workout => {
                // Extract total reps from saved workout data
                if (workout.totalReps) {
                    totalPastReps += workout.totalReps;
                } else if (workout.workoutData) {
                    // Try to extract from workout data string (Android format)
                    const repMatch = workout.workoutData.match(/Total Reps: (\d+)/);
                    if (repMatch) {
                        totalPastReps += parseInt(repMatch[1]);
                    }
                }
            });

            // Calculate bonus: 1 point per 10 past reps, capped at 100
            return Math.min(Math.floor(totalPastReps / 10), 100);
        } catch (error) {
            console.error('Error calculating Hidden Health bonus:', error);
            return 0;
        }
    };

    // Generate workout from battle code for display
    const generateWorkoutFromCode = () => {
        const exercises = [];
        if (!actualBattleCode) {
            console.warn('No battle code available for workout generation');
            return exercises;
        }

        const characters = actualBattleCode.split('');

        // Enhanced exercise mapping to match BattleWorkout
        const exerciseMap = {
            // Bodyweight characters
            '~': ['AirSquats', 'Lunges', 'StepUps', 'CalfRaises'],
            'w': ['PushUps', 'DiamondPushUps', 'PikePushUps'],
            '@': ['PullUps', 'ChinUps', 'AustralianPullUps'],
            'd': ['Plank', 'RussianTwists', 'MountainClimbers'],
            '^': ['PushUps', 'DiamondPushUps', 'TricepDips'],
            '_': ['PikePushUps', 'ElevatedPushUps', 'Plank'],

            // Advanced bodyweight characters
            'e': ['ClappingPushUps', 'DeclinePushUps'],
            'k': ['ChinUps', 'BackExtensions', 'AustralianPullUps'],
            'h': ['DumbbellSquatsandPress', 'PushUpJumps', 'ShoulderPress'],
            'm': ['ChairTricepExtension', 'FrontRackStretch', 'TricepDips'],
            'i': ['BicepCurls', 'PullUpsBarHold'],
            't': ['Dips', 'ChairTricepExtension', 'TricepDips'],
            'f': ['FarmerSquats', 'WristCurls'],
            'g': ['BoxJumps', 'BurpeeWithDumbbell', 'JumpingLunges'],
            'a': ['CrunchAndPunch', 'DeadBugs', 'KneeKicks'],
            'v': ['JumpSquats', 'CalfRaises', 'JumpingJacks'],
            'p': ['Shrugs', 'TrapRaises'],
            'q': ['SumoSquats', 'BodyweightSquats'],
            'n': ['DeadLift', 'HamstringCurls'],
            'u': ['GluteBridges', 'SingleLegGluteBridge'],
            'j': ['ClamshellExercise', 'HipCircles'],
            'y': ['WallSit', 'Lunges'],
            'z': ['SidePlank', 'RussianTwists'],
            '!': ['Burpees', 'Sprawls', 'MountainClimbers'],

            // Bands characters
            'l': ['G87DeadLift', 'BandedSquats', 'LegPress'],
            'c': ['G87DoorSmash', 'BandedChestPress', 'ChestFlyes'],
            'b': ['BandedRows', 'BandedLatPulldown', 'RearDeltFlyes'],
            'o': ['BandedWoodchops', 'BandedRussianTwists', 'BandedPlank'],
            'r': ['G87OneArmPunch', 'BandedBicepCurls', 'BandedTricepExtensions'],
            's': ['G87StepWide', 'G87OneArmOverheadPress', 'BandedShoulderPress'],

            // Gym characters
            '$': ['BenchPress', 'DumbbellFlyes', 'CableCrossovers'],
            'x': ['BentOverRows', 'LatPulldowns', 'Deadlifts'],
            '&': ['ArnoldPress', 'LateralRaises', 'Shrugs'],
            '*': ['ArmCurls', 'TricepExtensions', 'ArmPress'],
            '(': ['BicepCurls', 'HammerCurls', 'ConcentrationCurls'],
            ')': ['SkullCrushers', 'TricepDips', 'OverheadTricepExtension'],
            '+': ['PlankVariations', 'CableCrunches', 'AbCrunches'],
            '=': ['Squats', 'LegPress', 'LegCurls']
        };

        characters.forEach(char => {
            const muscleGroupExercises = exerciseMap[char] || ['PushUps', 'AirSquats'];
            const randomExercise = muscleGroupExercises[Math.floor(Math.random() * muscleGroupExercises.length)];
            exercises.push(randomExercise);
        });

        return exercises;
    };

    // Initialize on component mount
    useEffect(() => {
        // 🎵 EPIC BATTLE COMPLETE SOUND!
        soundManager.playSound('workoutComplete2', 0.9);

        // Try to get workout from session storage first
        try {
            const battleCompleteData = sessionStorage.getItem('battleCompleteData');
            if (battleCompleteData) {
                const data = JSON.parse(battleCompleteData);
                if (data.generatedWorkout && data.generatedWorkout.length > 0) {
                    console.log('Using workout from session storage:', data.generatedWorkout);
                    setGeneratedWorkout(data.generatedWorkout);
                } else {
                    setGeneratedWorkout(generateWorkoutFromCode());
                }
            } else {
                setGeneratedWorkout(generateWorkoutFromCode());
            }
        } catch (e) {
            console.log('Error loading workout from session, generating new one');
            setGeneratedWorkout(generateWorkoutFromCode());
        }

        // Calculate and apply Hidden Health bonus
        const calculatedBonus = calculateHiddenHealthBonus();
        setBonusPoints(calculatedBonus);

        if (calculatedBonus > 0) {
            setTimeout(() => {
                setAdjustedTotalScore(prev => prev + calculatedBonus);
                setBonusPointsApplied(true);
                // 🎵 BONUS POINTS SOUND
                soundManager.playSound('oneMore', 0.6);
            }, 1000);
        }

        // Hide confetti after 5 seconds
        const confettiTimer = setTimeout(() => {
            setShowConfetti(false);
        }, 5000);

        return () => clearTimeout(confettiTimer);
    }, [actualBattleCode]);

    // Get exercise counts for display
    const exerciseCounts = generatedWorkout.reduce((acc, exercise) => {
        acc[exercise] = (acc[exercise] || 0) + 1;
        return acc;
    }, {});

    // Determine workout type
    const getWorkoutType = () => {
        if (!actualBattleCode) return "Battle Mode";

        const firstChar = actualBattleCode[0];
        if ("ekhmatfgvpqnujyzbi".includes(firstChar)) return "Advanced BodyWeight";
        if (firstChar === 'x' || "$&".includes(firstChar)) return "Strength Builder";
        if ("lcbors".includes(firstChar)) return "Bands Builder";
        if ("~w@d^_".includes(firstChar)) return "BodyWeight Builder";
        if (actualBattleCode.length > 3 &&
            actualBattleCode.split('').some(c => "ekhmatfgvpqnujyz".includes(c)) &&
            actualBattleCode.split('').some(c => "~@d^_".includes(c))) {
            return "Everything All Moves";
        }
        return "Battle Mode";
    };

    // Save workout to localStorage
    const handleSaveWorkout = () => {
        // 🎵 SAVE BATTLE SOUND
        soundManager.playSound('oneMore2', 0.7);

        const workoutSummary = {
            id: Date.now(),
            date: new Date().toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            }),
            workoutType: 'Battle Mode',
            battleCode: actualBattleCode || 'unknown',
            muscleGroupCombination: actualBattleCode || 'unknown',
            exercises: Object.keys(exerciseCounts).map(exercise => {
                const count = exerciseCounts[exercise];
                return count > 1 ? `${exercise} (x${count})` : exercise;
            }),
            totalReps: parseInt(totalReps) || 0,
            totalSets: parseInt(sets) || 0,
            setCount: parseInt(sets) || 0,
            repCount: Math.floor((parseInt(totalReps) || 0) / Object.keys(exerciseCounts).length) || 15,
            totalScore: adjustedTotalScore,
            bonusPoints: bonusPoints,
            numberOfExercises: Object.keys(exerciseCounts).length,
            currentSet: parseInt(completedSets) || 0,
            difficultyDescription: 'Battle Mode',
            timestamp: new Date().toISOString(),
            // Include battle-specific data
            workoutData: `Workout Summary\n\n` +
                `Workout Type: ${getWorkoutType()}\n` +
                `Battle Code: ${actualBattleCode || 'unknown'}\n\n` +
                `Your Battle Workout:\n${Object.keys(exerciseCounts).map(exercise => {
                    const count = exerciseCounts[exercise];
                    return count > 1 ? `${exercise} (x${count})` : exercise;
                }).join('\n')}\n\n` +
                `Total Score: ${adjustedTotalScore}${bonusPoints > 0 ? ` (includes +${bonusPoints} Health Bonus)` : ''}\n` +
                `Total Reps: ${totalReps || 0}\n` +
                `Total Sets: ${sets || 0}\n` +
                `Number of Exercises: ${Object.keys(exerciseCounts).length}\n` +
                `Completed Sets: ${completedSets || 0}`
        };

        try {
            const savedWorkouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');
            savedWorkouts.push(workoutSummary);
            localStorage.setItem('workoutHistory', JSON.stringify(savedWorkouts));

            // Also save the battle code for future reference
            localStorage.setItem('lastBattleCode', actualBattleCode || 'unknown');

            alert('🎮 Battle Workout Saved! Your victory has been recorded! ⚔️');
        } catch (error) {
            console.error('Error saving workout:', error);
            alert('Error saving workout. Please try again.');
        }
    };

    // Do another set
    const handleAnotherSet = () => {
        // 🎵 NEW BATTLE SOUND
        soundManager.playSound('gameIntroMode2', 0.8);

        if (actualBattleCode) {
            const encodedBattleCode = encodeURIComponent(actualBattleCode);
            navigate(`/battle-rep-count/${encodedBattleCode}`);
        } else {
            navigate('/battle-selection');
        }
    };

    // Back to home
    const handleBackToHome = () => {
        // 🎵 BACK TO HOME SOUND
        soundManager.playSound('next', 0.6);

        // Clear battle-related session data
        sessionStorage.removeItem('currentBattleCode');
        sessionStorage.removeItem('battleWorkoutProgress');
        sessionStorage.removeItem('battleCompleteData');
        navigate('/');
    };

    return (
        <div
            className="battle-complete-container"
            style={{ backgroundImage }}
        >
            {/* Battle overlay */}
            <div className="battle-complete-overlay"></div>

            {/* Epic Battle Smoke & Sparks */}
            {showConfetti && (
                <div className="battle-confetti">
                    <div className="victory-sparks"></div>
                </div>
            )}

            <div className="battle-complete-content">
                {/* Victory header */}
                <header className="battle-complete-header">
                    <h1 className="victory-title">🎮 BATTLE COMPLETE! 🎮</h1>
                    <div className="score-display">
                        <span className="total-score">Total Score: {adjustedTotalScore}</span>
                        {bonusPoints > 0 && (
                            <div className="hidden-health-bonus">
                                <span className="bonus-points">(+{bonusPoints} Hidden Health Bonus)</span>
                                <span className="bonus-text">Bonus from your past workout history!</span>
                            </div>
                        )}
                    </div>
                </header>

                {/* Battle stats - FIXED to show correct values */}
                <div className="battle-stats-card">
                    <div className="battle-stat">
                        <span className="stat-value">{Object.keys(exerciseCounts).length}</span>
                        <span className="stat-label">Unique Moves</span>
                    </div>
                    <div className="battle-stat">
                        <span className="stat-value">{totalReps || 0}</span>
                        <span className="stat-label">Total Reps</span>
                    </div>
                    <div className="battle-stat">
                        <span className="stat-value">{sets || 0}</span>
                        <span className="stat-label">Total Sets</span>
                    </div>
                    <div className="battle-stat">
                        <span className="stat-value">{completedSets || 0}</span>
                        <span className="stat-label">Completed Sets</span>
                    </div>
                </div>

                {/* Workout details */}
                <div className="workout-details-card">
                    <h2>⚔️ Your Battle Workout ⚔️</h2>
                    <div className="workout-type">
                        <span className="type-label">Battle Type:</span>
                        <span className="type-value">{getWorkoutType()}</span>
                    </div>
                    <div className="battle-code-display">
                        <span className="code-label">Battle Code:</span>
                        <span className="code-value">{actualBattleCode || 'Code Missing'}</span>
                        {!actualBattleCode && (
                            <span className="code-error"> (Check navigation from BattleWorkout)</span>
                        )}
                    </div>

                    <div className="exercises-list">
                        {Object.entries(exerciseCounts).map(([exercise, count], index) => (
                            <div key={index} className="exercise-item">
                                <span className="exercise-number">{index + 1}</span>
                                <span className="exercise-name">
                                    {count > 1 ? `${exercise} (x${count})` : exercise}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action buttons */}
                <div className="battle-action-buttons">
                    <button
                        className="battle-button email-button-primary"
                        onClick={handleEmailWorkout}
                    >
                        <span className="button-icon">📧</span>
                        <span className="button-text">Email My Battle Results</span>
                    </button>

                    <button
                        className="battle-button do-another-set"
                        onClick={handleAnotherSet}
                    >
                        <span className="button-icon">🔥</span>
                        <span className="button-text">Do A New Set</span>
                    </button>

                    <button
                        className="battle-button save-workout"
                        onClick={handleSaveWorkout}
                    >
                        <span className="button-icon">💾</span>
                        <span className="button-text">Save Battle</span>
                    </button>

                    <button
                        className="battle-button back-home"
                        onClick={handleBackToHome}
                    >
                        <span className="button-icon">🏠</span>
                        <span className="button-text">Back to Home</span>
                    </button>
                </div>
            </div>

            {/* Email Modal */}
            {showEmailModal && (
                <div className="modal-overlay">
                    <div className="battle-email-modal">
                        <h3>📧 Email Your Battle Results</h3>

                        <div className="modal-content">
                            <div className="input-group">
                                <label>Email Address:</label>
                                <input
                                    type="email"
                                    value={userEmail}
                                    onChange={(e) => setUserEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    disabled={isSubmitting}
                                    autoFocus
                                    onKeyPress={(e) => e.key === 'Enter' && handleSubmitEmail()}
                                />
                            </div>

                            <div className="modal-preview">
                                <h4>What you'll receive:</h4>
                                <div className="workout-preview">
                                    <p>🎮 Complete battle summary with score</p>
                                    <p>⚔️ All exercises and battle stats</p>
                                    <p>🏆 Hidden Health Bonus details</p>
                                    <p>🔥 BUMFIT branding and battle code</p>

                                    <div className="signature-preview">
                                        🔥 BUMFIT - Train - Build - Battle 🔥<br/>
                                        bumfit.com - Where every word is a workout!<br/>
                                        Unlimited workouts at your fingertips.
                                    </div>
                                </div>
                            </div>

                            <div className="modal-buttons">
                                <button
                                    className="send-button"
                                    onClick={handleSubmitEmail}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Sending...' : '📧 Send Battle Results'}
                                </button>

                                <button
                                    className="cancel-button"
                                    onClick={() => {
                                        setShowEmailModal(false);
                                        setUserEmail('');
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BattleComplete;